import React, { useState, useEffect, useContext } from "react";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance"; // Assuming you have an axios instance for API requests
import Pricing from "../components/Pricing";
import AuthContext from "../AuthContext";
import { FaSpinner } from "react-icons/fa"; 

Modal.setAppElement("#root");

const Register = () => {
  const [formData, setFormData] = useState({
    RestaurantName: "",
    OwnerName: "",
    email: "",
    password: "",
    phone: "",
    address: "",
    restaurantTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [isFormValid, setIsFormValid] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [registering, setRegistering] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  //call a api to say register is clicked
  useEffect(() => {
    const registerClicked = async () => {
      try {
         await axiosInstance.get("/api/registerclicked");
      } catch (error) {
        console.error("Error fetching visitor count:", error);
      }
    };

    registerClicked();
  }, []);


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]+$/;
    return phoneRegex.test(phone);
  };

  // Validate form when formData changes
  useEffect(() => {
    const { RestaurantName, OwnerName, email, phone, password, address } =
      formData;
    const isFormFilled =
      RestaurantName && OwnerName && email && phone && password && address;
    const isValidPhone = validatePhone(phone);

    setIsPhoneValid(isValidPhone);
    setIsFormValid(isFormFilled && isValidPhone);
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setRegistering(true);
    try {
      const response = await axiosInstance.post("/api/register", formData);
      login(response.data.UserObj);

      setMessage("Registration successful! You can now add payment method.");
      setMessageType("success");
      setShowMessageModal(true);
    } catch (error) {
      setMessage("An error occurred during registration. Please try again.");
      setMessageType("error");
      setShowMessageModal(true);
    }
    setRegistering(false);
  };

  const closeModal = () => {
    setShowMessageModal(false);
    if (messageType === "success") {
      navigate("/login");
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-4">Register Your Restaurant</h1>
      <form onSubmit={handleSubmit}>
        {/* Restaurant Name */}
        <div className="mb-4">
          <label
            className="block text-gray-700 font-bold mb-2"
            htmlFor="RestaurantName"
          >
            Restaurant Name
          </label>
          <input
            type="text"
            name="RestaurantName"
            id="RestaurantName"
            value={formData.RestaurantName}
            onChange={handleChange}
            className="w-full bg-white text-black border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Your restaurant name"
            required
          />
        </div>

        {/* Owner Name */}
        <div className="mb-4">
          <label
            className="block text-gray-700 font-bold mb-2"
            htmlFor="OwnerName"
          >
            Owner Name
          </label>
          <input
            type="text"
            name="OwnerName"
            id="OwnerName"
            value={formData.OwnerName}
            onChange={handleChange}
            className="w-full bg-white text-black border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Owner name"
            required
          />
        </div>

        {/* Email */}
        <div className="mb-4">
          <label className="block text-gray-700 font-bold mb-2" htmlFor="email">
            Email
          </label>
          <input
            type="email"
            name="email"
            id="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full bg-white text-black border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Email"
            required
          />
        </div>

        {/* Password */}
        <div className="mb-4">
          <label
            className="block text-gray-700 font-bold mb-2"
            htmlFor="password"
          >
            Password
          </label>
          <input
            type="password"
            name="password"
            id="password"
            value={formData.password}
            onChange={handleChange}
            className="w-full bg-white text-black border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Password"
            required
          />
        </div>

        {/* Phone */}
        <div className="mb-4">
          <label className="block text-gray-700 font-bold mb-2" htmlFor="phone">
            Phone
          </label>
          <input
            type="tel"
            name="phone"
            id="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full bg-white text-black border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              !isPhoneValid ? "border-red-500" : ""
            }`}
            placeholder="Phone number"
            required
          />
          {!isPhoneValid && (
            <p className="text-red-500 text-xs italic">
              Phone number must be numeric.
            </p>
          )}
        </div>

        {/* Address */}
        <div className="mb-4">
          <label
            className="block text-gray-700 font-bold mb-2"
            htmlFor="address"
          >
            Address
          </label>
          <input
            type="text"
            name="address"
            id="address"
            value={formData.address}
            onChange={handleChange}
            className="w-full bg-white text-black border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Restaurant address"
            required
          />
        </div>

        {/* Submit button */}
        <div className="flex items-center justify-between">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            disabled={!isFormValid || registering}
          >
            {registering ? (
              <FaSpinner size={25} className="animate-spin text-white inline-block" />
            ) : (
              "Register"
            )}
          </button>
        </div>
      </form>

      {/* Message Modal */}
      <Modal
        isOpen={showMessageModal}
        onRequestClose={closeModal}
        contentLabel="Registration Status"
        className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75"
      >
        <div className="bg-white rounded-lg p-8">
          <h2
            className={`text-2xl font-bold mb-4 ${
              messageType === "success" ? "text-green-600" : "text-red-600"
            }`}
          >
            {messageType === "success" ? "Success!" : "Error"}
          </h2>
          <p>{message}</p>

          {messageType === "success" && (
            <Pricing email={formData.email} duringRegistration={true} />
          )}

          <button
            onClick={closeModal}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mt-4"
          >
            Later
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Register;
